import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Helmet } from "react-helmet";
import { useConfirmMutation } from "_services/auth";
import { userApi } from "_services/user";
import { useAppDispatch } from "store";
import { authSetToken } from "_utils/slices/authSlice";
import { errorCopy, useCopy } from "./ui";
import "./style.css";

export default function ConfirmEmail() {
    const { c } = useCopy();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [confirm, { isLoading }] = useConfirmMutation();
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const submit = async () => {
        setBusy(true);
        setError("");
        let verified = confirmed;
        try {
            if (!verified) {
                const response = await confirm(token).unwrap();
                dispatch(authSetToken(response));
                setConfirmed(true);
                verified = true;
            }
            await dispatch(userApi.endpoints.getInfos.initiate(null, { forceRefetch: true, subscribe: false })).unwrap();
            navigate("/app/advisor", { replace: true });
        } catch (failure) {
            setError(verified
                ? c("Your email is confirmed, but we couldn’t refresh your profile. Retry to continue; the confirmation link will not be submitted again.", "Votre e-mail est confirmé, mais votre profil n’a pas pu être actualisé. Réessayez pour continuer ; le lien de confirmation ne sera pas soumis à nouveau.")
                : [400, 401, 404, 410].includes(failure.status)
                    ? c("This verification link is invalid, expired or already used. Sign in and request a new verification email.", "Ce lien de vérification est invalide, expiré ou déjà utilisé. Connectez-vous et demandez un nouvel e-mail de vérification.")
                    : errorCopy({ status: failure.status, serverMessage: failure.data?.message || failure.data?.error }, c));
        } finally { setBusy(false); }
    };
    return <main className="aw-auth-page">
        <Helmet><title>{c("Confirm your email", "Confirmer votre e-mail")} · Thesis.center</title><meta name="referrer" content="no-referrer" /></Helmet>
        <section className="aw-confirm-card">
            <div className="aw-eyebrow">THESIS.CENTER / {c("YOUR ACCOUNT", "VOTRE COMPTE")}</div>
            <h1>{confirmed ? c("Email confirmed.", "E-mail confirmé.") : c("One last step. Then, research.", "Une dernière étape. Puis, la recherche.")}</h1>
            <p>{c("Confirm your email to connect safely with your advisors and students. Nothing changes until you press the button below.", "Confirmez votre e-mail pour échanger en toute sécurité avec vos encadrants et étudiants. Rien ne change avant votre confirmation ci-dessous.")}</p>
            {!token && <p className="aw-error" role="alert">{c("This link is missing a verification token. Open the complete link from your email or request a new one.", "Ce lien ne contient pas de jeton de vérification. Ouvrez le lien complet reçu par e-mail ou demandez-en un nouveau.")}</p>}
            {error && <p className="aw-error" role="alert">{error}</p>}
            {token && <button type="button" className="aw-primary" disabled={busy || isLoading} onClick={submit}>{busy || isLoading ? c("Confirming…", "Confirmation…") : confirmed ? c("Retry profile refresh", "Réessayer l’actualisation") : c("Confirm email", "Confirmer l’e-mail")}</button>}
            <Link to="/login" className="aw-doc-link">{c("Back to sign in", "Retour à la connexion")} →</Link>
        </section>
    </main>;
}
